import { Link } from 'react-router-dom';
import {
  audioNotes,
  clockTime,
  formatDuration,
  livePhotos,
  readingLabel,
  type Evidence,
} from '@kilroy/shared';
import { IconCompass, IconStrata } from './icons';
import './evidence-row.css';

/* One record in the day.
 *
 * The title is composed, not typed — nobody names an outcrop while standing on
 * it. Type plus tags is what would have gone in the notebook margin anyway,
 * so the list is scannable without anyone having done extra work in the field.
 */

export function describeEvidence(e: Evidence): string {
  if (e.evidence_type === 'structural_feature' && e.structural_feature) {
    const f = e.structural_feature;
    return [f.feature_type.replace(/_/g, ' '), readingLabel(f)].filter(Boolean).join(' · ');
  }
  if (e.evidence_type === 'rock_observation' && e.rock_observation) {
    const r = e.rock_observation;
    const tags = [
      ...new Set([r.rock_type, ...(r.alteration_type ?? []), r.texture_type].filter(Boolean)),
    ];
    return tags.length ? tags.join(' · ') : 'Rock observation';
  }
  return e.evidence_type.replace(/_/g, ' ');
}

/** Below this, a record counts toward the Phase 4 training-grade dial. */
const TRAINING_GRADE_M = 10;

export function EvidenceRow({ evidence: e }: { evidence: Evidence }) {
  const photos = livePhotos(e);
  const audio = audioNotes(e);
  const structural = e.evidence_type === 'structural_feature';
  const synced = e.sync_state === 'synced';
  const loose = e.gps_accuracy_m > TRAINING_GRADE_M;

  const meta = [
    e.gps_accuracy_m < 0 ? null : `±${Math.round(e.gps_accuracy_m)} m`,
    e.structural_feature?.reading_source,
    photos.length ? `${photos.length} photo${photos.length === 1 ? '' : 's'}` : null,
    audio.length ? formatDuration(audio.reduce((n, a) => n + (a.duration ?? 0), 0)) : null,
  ].filter(Boolean) as string[];

  return (
    <Link to={`/evidence/${e.id}`} className="rec">
      <span className="rec__t">{clockTime(e.captured_at)}</span>
      <span className={`ic ic--sm ${structural ? 'ic--brand' : 'ic--win'}`}>
        {structural ? <IconCompass size={13} /> : <IconStrata size={13} />}
      </span>
      <span className="rec__b">
        <div className="rec__n">{describeEvidence(e)}</div>
        <div className="rec__m">
          {meta.map((m, i) => (
            <span key={m + i} className={i === 0 && loose ? 'rec__loose' : undefined}>
              {i > 0 && ' · '}
              {m}
            </span>
          ))}
        </div>
      </span>
      {/* Synced is the resting state and says nothing. A mark appears when
          something is still waiting on you. */}
      <span className={`dot${synced ? '' : ' dot--wait'}`} aria-label={synced ? 'synced' : 'queued'} />
    </Link>
  );
}
